import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import { AppState } from 'services/types';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import replaceNull from 'utils/replace-null';
import {
    FertilizerProduct,
    FertilizerProductId,
    FertilizerProductsProps,
    FertilizerProductsState
} from './types';

const selectFertilizerProducts = (state: AppState): FertilizerProductsState =>
    state.fertilizerProducts;

const selectFertilizerProductId = (
    state: AppState,
    props: FertilizerProductsProps
): FertilizerProductId => props.productId;

const selectFertilizerProductsKeyedById = createDeepEqualSelector(
    selectFertilizerProducts,
    (fertilizerProducts) => keyBy(fertilizerProducts, 'id')
);

const selectFertilizerProduct = createDeepEqualSelector(
    selectFertilizerProductsKeyedById,
    selectFertilizerProductId,
    (fertilizerProducts, productId) => get(fertilizerProducts, productId)
);

const makeSelectFertilizerProductValue = <T extends keyof FertilizerProduct>(
    fieldName: T
) =>
    createDeepEqualSelector(selectFertilizerProduct, (fertilizerProduct) =>
        get(fertilizerProduct, fieldName)
    );

const selectFertilizerProductsArray = createDeepEqualSelector(
    selectFertilizerProducts,
    (fertilizerProducts) => Object.values(fertilizerProducts)
);

const selectFertilizerProductNPKValue = createDeepEqualSelector(
    makeSelectFertilizerProductValue('nitrogenValue'),
    makeSelectFertilizerProductValue('phosphorusValue'),
    makeSelectFertilizerProductValue('potassiumValue'),
    (nitrogenValue, phosphorusValue, potassiumValue) =>
        `N:P:K - ${replaceNull(nitrogenValue)}:${replaceNull(
            phosphorusValue
        )}:${replaceNull(potassiumValue)}`
);

const selectFertilizerProductSMgCaValue = createDeepEqualSelector(
    makeSelectFertilizerProductValue('sulfurValue'),
    makeSelectFertilizerProductValue('magnesiumValue'),
    makeSelectFertilizerProductValue('calciumValue'),
    (sulfurValue, magnesiumValue, calciumValue) =>
        `S:Mg:Ca - ${replaceNull(sulfurValue)}:${replaceNull(
            magnesiumValue
        )}:${replaceNull(calciumValue)}`
);

const makeSelectFertilizerProductNPKValue =
    (): typeof selectFertilizerProductNPKValue =>
        selectFertilizerProductNPKValue;

const makeSelectFertilizerProductSMgCaValue =
    (): typeof selectFertilizerProductSMgCaValue =>
        selectFertilizerProductSMgCaValue;

export {
    selectFertilizerProducts,
    selectFertilizerProductId,
    selectFertilizerProduct,
    selectFertilizerProductsArray,
    selectFertilizerProductsKeyedById,
    makeSelectFertilizerProductValue,
    makeSelectFertilizerProductNPKValue,
    makeSelectFertilizerProductSMgCaValue
};
